import React, { useEffect, useState, useCallback, useRef } from "react";
import TablePagesTitle from "../../../components/table-pages-title/TablePagesTitle";
import AccessPageAction from "../../accessmanagment/page-action/AccessPageAction";
import { Link } from "react-router-dom";
import { showToast } from "../../../utils/utils";
import { FaSpinner, FaSync, FaSyncAlt } from "react-icons/fa";
import { aiData, checklist, tooltips, aiReportData } from "../../../locales/en";
import {
  AI_TRAINING_DATA,
  DOWNLOAD_INVOICE,
  FT_CREDIT_SCORE_DATA,
  PROCESSING_STATUS,
  PENDING_STATUS
} from "../../../utils/constants/appConstants";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "./aiRepostStyle.scss";
import { showToastType } from "../../../utils/constants/appConstants";
import {
  getReportFileDownload,
  refreshAiReportData,
  refreshAiReportDataGet,
  setAiRequestDataStatus
} from "../../../redux/userReportsSlice";
import { useDispatch, useSelector } from "react-redux";
import Style from "./../../../assets/scss/custom.module.scss";
import AIInsightsModal from "../../../components/modals/AIInsightsModal";

const getLoaderKey = (reportFileType) => {
  if (reportFileType === AI_TRAINING_DATA) {
    return "aiData";
  }
  if (reportFileType === DOWNLOAD_INVOICE) {
    return "invoice";
  }
  return "credit";
};

const getReportFileName = (reportFileType) => {
  if (reportFileType === DOWNLOAD_INVOICE) {
    return "invoice";
  }
  if (reportFileType === FT_CREDIT_SCORE_DATA) {
    return "credit_score";
  }
  return "AI_training_data";
};

const AiData = () => {
  const dispatch = useDispatch();
  const { aiRequestDataStatus } = useSelector((state) => state.userReport);

  const [loadingButton, setLoadingButton] = useState("");
  const [aiModal, setAiModal] = useState(false);

  const closeAiModal = () => setAiModal(false);

  const fetchRef = useRef();

  const tooltipsArray = [
    { id: "tooltip-resend", content: tooltips.refreshAiScore },
    { id: "tooltip-invoice", content: tooltips.refreshDownloadInvoice },
    { id: "tooltip-credit", content: tooltips.searchCreditScore }
  ];

  // Generic File Download handler
  const handleAiDataDownload = async (reportFileType) => {
    const loaderKey = getLoaderKey(reportFileType);
    const fileName = getReportFileName(reportFileType);
    try {
      setLoadingButton(loaderKey);
      const response = await dispatch(
        getReportFileDownload({ reportFileType, fileName })
      ).unwrap();
      if (response?.status === 200) {
        showToast(checklist?.errors?.fileDownload, showToastType.success);
      }
    } catch {
      showToast(aiReportData.fetchError, showToastType.error);
    } finally {
      setLoadingButton("");
    }
  };

  // Handle Refresh Report Generation
  const handleRefreshReport = async (reportFileType) => {
    setAiModal(true);

    try {
      const response = await dispatch(
        refreshAiReportData(reportFileType)
      ).unwrap();

      if (response?.status === 200 || response?.status === 201) {
        const reportData = response?.data?.data;

        // Update status with the response data
        const currentStatus = { ...aiRequestDataStatus };
        currentStatus[reportFileType] = {
          ...reportData,
          isProcessing:
            reportData?.status === PROCESSING_STATUS ||
            reportData?.status === PENDING_STATUS
        };
        dispatch(setAiRequestDataStatus(currentStatus));
      }
    } catch {
      showToast(aiReportData.fetchError, showToastType.error);
    }
  };

  // Fetch initial status and set up continuous polling
  const fetchAllStatuses = useCallback(async () => {
    const reportTypes = [
      AI_TRAINING_DATA,
      DOWNLOAD_INVOICE,
      FT_CREDIT_SCORE_DATA
    ];
    const statusUpdates = {};

    // Fetch all statuses in parallel
    const promises = reportTypes.map(async (reportType) => {
      try {
        const response = await dispatch(
          refreshAiReportDataGet(reportType)
        ).unwrap();
        statusUpdates[reportType] = response?.data?.data;
      } catch {
        showToast(aiReportData.fetchError, showToastType.error);
      }
    });

    await Promise.all(promises);

    if (Object.keys(statusUpdates).length > 0) {
      dispatch(setAiRequestDataStatus(statusUpdates));
    }
  }, [dispatch]);

  useEffect(() => {
    fetchRef.current = fetchAllStatuses;
  }, [fetchAllStatuses]);

  useEffect(() => {
    fetchRef.current?.();

    const intervalId = setInterval(() => {
      fetchRef.current?.();
    }, 25000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <>
      <TablePagesTitle mainTitle={aiData?.mainTitle} />
      <AccessPageAction subheading={aiData?.mainTitle} />
      <div className="row mb-2 mx-0">
        <div className="col-12 sales-page-title-wrap sub-heading-blog-wrap border-0 pb-2 px-0">
          <div className="row box-heading">
            <div className="col-md-12">
              <ul
                className={`${Style.searchOptionWrap} d-flex flex-wrap align-items-center`}
              >
                <li className="my-1 ai-action-item">
                  <div className="action-content">
                    <Link
                      to=""
                      className={`btn btn-primary spinDiv ${loadingButton === "aiData" ? "spinDivBlack" : ""}`}
                      onClick={() => handleAiDataDownload(AI_TRAINING_DATA)}
                    >
                      {aiData?.btnGrps.btn1}
                      {loadingButton === "aiData" && (
                        <span className="loadSpin">
                          <FaSpinner />
                        </span>
                      )}
                    </Link>
                    <span className="timestamp-text">
                      {aiRequestDataStatus?.AI_TRAINING_DATA?.isProcessing
                        ? aiReportData.processing
                        : `${aiReportData.updatedPrefix} ${aiRequestDataStatus?.AI_TRAINING_DATA?.completedTime || aiReportData.processing}`}
                    </span>
                    <button
                      data-tooltip-id="tooltip-resend"
                      type="button"
                      className="btn btn-primary small-action-btn"
                      onClick={() => handleRefreshReport(AI_TRAINING_DATA)}
                      disabled={
                        aiRequestDataStatus?.AI_TRAINING_DATA?.isProcessing
                      }
                    >
                      <FaSyncAlt
                        className={
                          aiRequestDataStatus?.AI_TRAINING_DATA?.isProcessing
                            ? aiReportData.spinningClass
                            : ""
                        }
                      />
                    </button>
                  </div>
                </li>

                <li className="my-1 ai-action-item">
                  <div className="action-content">
                    <Link
                      to=""
                      className={`btn btn-primary spinDiv ${loadingButton === "invoice" ? "spinDivBlack" : ""}`}
                      onClick={() => handleAiDataDownload(DOWNLOAD_INVOICE)}
                    >
                      {aiData?.btnGrps.btn4}
                      {loadingButton === "invoice" && (
                        <span className="loadSpin">
                          <FaSpinner />
                        </span>
                      )}
                    </Link>
                    <span className="timestamp-text">
                      {aiRequestDataStatus?.DOWNLOAD_INVOICE?.isProcessing
                        ? aiReportData.processing
                        : `${aiReportData.updatedPrefix} ${aiRequestDataStatus?.DOWNLOAD_INVOICE?.completedTime || aiReportData.processing}`}
                    </span>
                    <button
                      data-tooltip-id="tooltip-invoice"
                      type="button"
                      className="btn btn-primary small-action-btn"
                      onClick={() => handleRefreshReport(DOWNLOAD_INVOICE)}
                      disabled={
                        aiRequestDataStatus?.DOWNLOAD_INVOICE?.isProcessing
                      }
                    >
                      <FaSync
                        className={
                          aiRequestDataStatus?.DOWNLOAD_INVOICE?.isProcessing
                            ? aiReportData.spinningClass
                            : ""
                        }
                      />
                    </button>
                  </div>
                </li>
                <li className="my-1 ai-action-item">
                  <div className="action-content">
                    <Link
                      to=""
                      className={`btn btn-primary spinDiv ${loadingButton === "credit" ? "spinDivBlack" : ""}`}
                      onClick={() => handleAiDataDownload(FT_CREDIT_SCORE_DATA)}
                    >
                      {aiData?.btnGrps.btn5}
                      {loadingButton === "credit" && (
                        <span className="loadSpin">
                          <FaSpinner />
                        </span>
                      )}
                    </Link>
                    <span className="timestamp-text">
                      {aiRequestDataStatus?.FT_CREDIT_SCORE_DATA?.isProcessing
                        ? aiReportData.processing
                        : `${aiReportData.updatedPrefix} ${aiRequestDataStatus?.FT_CREDIT_SCORE_DATA?.completedTime || aiReportData.processing}`}
                    </span>
                    <button
                      data-tooltip-id="tooltip-credit"
                      type="button"
                      className="btn btn-primary small-action-btn"
                      onClick={() => handleRefreshReport(FT_CREDIT_SCORE_DATA)}
                      disabled={
                        aiRequestDataStatus?.FT_CREDIT_SCORE_DATA?.isProcessing
                      }
                    >
                      <FaSync
                        className={
                          aiRequestDataStatus?.FT_CREDIT_SCORE_DATA
                            ?.isProcessing
                            ? aiReportData.spinningClass
                            : ""
                        }
                      />
                    </button>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {tooltipsArray.map(({ id, content }) => (
        <ReactTooltip
          key={id}
          className="tooltipcustom"
          id={id}
          place="top"
          content={content}
        />
      ))}
      <AIInsightsModal isOpen={aiModal} toggle={closeAiModal} />
    </>
  );
};

export default AiData;
